/**
 * Performance utilities for mobile and low-power devices.
 * Use these to disable or reduce heavy effects (particles, parallax, mousemove) for smoother 60 FPS.
 */

import { useState, useEffect } from 'react';

const MOBILE_BREAKPOINT = 768;
const TOUCH_QUERY = '(hover: none) and (pointer: coarse)';

/**
 * True if viewport is mobile-sized (e.g. < 768px).
 * Safe to call in browser; returns false in SSR.
 */
export function isMobile() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_BREAKPOINT;
}

/**
 * True if the device has a coarse pointer (touch) as primary input.
 * Use to skip mousemove-driven effects (no benefit on touch, saves re-renders).
 */
export function isTouchDevice() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(TOUCH_QUERY).matches;
}

/**
 * True if user prefers reduced motion (accessibility / low-power).
 */
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Whether to fully disable heavy decorative effects (particles, animated orbs).
 * Only true when user explicitly prefers reduced motion (accessibility).
 * On mobile we still show effects in a lighter form.
 */
export function shouldReduceMotion() {
  if (typeof window === 'undefined') return false;
  return prefersReducedMotion();
}

/** Hook: true only when user prefers reduced motion (accessibility). Effects still show on mobile in lighter form. */
export function useReduceMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    setReduce(prefersReducedMotion());
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const listener = () => setReduce(prefersReducedMotion());
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, []);
  return reduce;
}

/** Hook: true when viewport is mobile-sized. Use for lighter variants (fewer particles, simpler orbs), not for hiding. Defaults to true so first paint on mobile is lightweight. */
export function useIsMobile() {
  const [mobile, setMobile] = useState(true);
  useEffect(() => {
    const update = () => setMobile(window.innerWidth < MOBILE_BREAKPOINT);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return mobile;
}

/** Hook: true after mount when device is touch-only. Use to skip mousemove listeners. */
export function useIsTouchDevice() {
  const [touch, setTouch] = useState(false);
  useEffect(() => {
    setTouch(isTouchDevice());
    const mq = window.matchMedia(TOUCH_QUERY);
    const listener = () => setTouch(isTouchDevice());
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, []);
  return touch;
}
